import React, { useState, useEffect, useCallback } from "react";
import { FiHome, FiRefreshCw, FiUsers, FiBarChart2, FiShield } from "react-icons/fi";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "./SEO";
import "./AdminPanel.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

const PLAN_OPTIONS = ["free", "light", "pro", "business"];

function planLabel(plan, t) {
  if (plan === "light") return t.planLight || "Light";
  if (plan === "pro" || plan === "paid") return t.planPro || "Pro";
  if (plan === "business") return t.planBusiness || "Business";
  return t.planFree || "Free";
}

export default function AdminPanel({ onBack }) {
  const { session } = useAuth();
  const { texts } = useLanguage();
  const t = texts?.admin || {};
  const ta = texts?.auth || {};
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [budgetInput, setBudgetInput] = useState("");
  const [busy, setBusy] = useState(false);

  const authHeaders = useCallback(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${session?.access_token || ""}`,
    }),
    [session?.access_token]
  );

  const load = useCallback(async () => {
    if (!API_BASE || !session?.access_token) {
      setError(t.notConfigured || "API base / login not configured");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [uRes, sRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/users`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/admin/stats`, { headers: authHeaders() }),
      ]);
      const uData = await uRes.json().catch(() => ({}));
      const sData = await sRes.json().catch(() => ({}));
      if (!uRes.ok) throw new Error(uData.error || "Failed to load users");
      if (!sRes.ok) throw new Error(sData.error || "Failed to load stats");
      setUsers(uData.users || []);
      setStats(sData);
      setBudgetInput(String(sData.freeBudget ?? ""));
    } catch (err) {
      setError(err.message || "Load failed");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, session?.access_token, t.notConfigured]);

  useEffect(() => {
    load();
  }, [load]);

  const changePlan = async (id, plan) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${id}/plan`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ plan }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Failed");
      }
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, plan } : u)));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const resetQuota = async (id) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${id}/reset-quota`, {
        method: "POST",
        headers: authHeaders(),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Failed");
      }
      setUsers((prev) =>
        prev.map((u) =>
          u.id === id ? { ...u, ai_edits_count: 0, ai_edits_period: "" } : u
        )
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const saveBudget = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/settings`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ freeBudgetMonthly: Number(budgetInput) }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Failed");
      }
      setStats((prev) => (prev ? { ...prev, freeBudget: Number(budgetInput) } : prev));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin-panel">
      <SEO title={`${texts.landing?.title || "FrontPlay"} | ${t.title || "Admin"}`} description={t.title || "Admin"} />
      <header className="admin-header">
        <div className="admin-header-inner">
          <h1 className="admin-title"><FiShield /> {t.title || "Admin"}</h1>
          <button type="button" className="admin-back" onClick={onBack} title={t.back || "Back"}>
            <FiHome /> {t.back || "Back"}
          </button>
        </div>
      </header>

      <div className="admin-body">
        {error ? <p className="admin-error" role="alert">{error}</p> : null}

        <section className="admin-section">
          <h2 className="admin-section-title"><FiBarChart2 /> {t.stats || "Stats"}</h2>
          {stats ? (
            <div className="admin-stats">
              <div className="admin-stat">
                <span className="admin-stat-label">{t.totalUsers || "Total users"}</span>
                <span className="admin-stat-value">{stats.totalUsers}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{t.planFree || "Free"}</span>
                <span className="admin-stat-value">{stats.byPlan?.free || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{t.planLight || "Light"}</span>
                <span className="admin-stat-value">{stats.byPlan?.light || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{t.planPro || "Pro"}</span>
                <span className="admin-stat-value">{stats.byPlan?.pro || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{ta.planBusiness || "Business"}</span>
                <span className="admin-stat-value">{stats.byPlan?.business || 0}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{t.monthEdits || "Edits this month"}</span>
                <span className="admin-stat-value">{stats.monthEdits}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-label">{t.freeBudget || "Free budget used"}</span>
                <span className="admin-stat-value">{stats.freeUsed} / {stats.freeBudget}</span>
              </div>
              <div className="admin-stat admin-stat--full">
                <span className="admin-stat-label">{t.freeBudgetSetting || "Free monthly budget"}</span>
                <span className="admin-stat-value">
                  <input
                    type="number"
                    className="admin-input"
                    min={0}
                    value={budgetInput}
                    onChange={(e) => setBudgetInput(e.target.value)}
                    disabled={busy}
                  />
                  <button type="button" className="admin-btn" onClick={saveBudget} disabled={busy || budgetInput === ""}>
                    {t.save || "Save"}
                  </button>
                </span>
              </div>
            </div>
          ) : null}
        </section>

        <section className="admin-section">
          <h2 className="admin-section-title">
            <FiUsers /> {t.users || "Users"}
            <button type="button" className="admin-refresh" onClick={load} disabled={loading} title={t.refresh || "Refresh"}>
              <FiRefreshCw />
            </button>
          </h2>
          {loading ? (
            <p className="admin-empty">{t.loading || "Loading…"}</p>
          ) : users.length === 0 ? (
            <p className="admin-empty">{t.noUsers || "No users"}</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t.email || "Email"}</th>
                    <th>{t.plan || "Plan"}</th>
                    <th>{t.usage || "Usage this month"}</th>
                    <th>{t.created || "Created"}</th>
                    <th>{t.actions || "Actions"}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td className="admin-email">
                        {u.email || u.id}
                        {u.is_admin ? <span className="admin-tag">{t.adminTag || "admin"}</span> : null}
                      </td>
                      <td>
                        <select
                          className="admin-select"
                          value={u.plan === "paid" ? "pro" : u.plan}
                          onChange={(e) => changePlan(u.id, e.target.value)}
                          disabled={busy || u.is_admin}
                          title={u.is_admin ? (t.cannotChangeAdmin || "Cannot change admin") : undefined}
                        >
                          {PLAN_OPTIONS.map((p) => (
                            <option key={p} value={p}>{planLabel(p, ta)}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {typeof u.ai_edits_count_current === "number"
                          ? u.ai_edits_count_current
                          : Number(u.ai_edits_count) || 0}
                      </td>
                      <td className="admin-muted">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="admin-btn admin-btn--small"
                          onClick={() => resetQuota(u.id)}
                          disabled={busy}
                        >
                          {t.resetQuota || "Reset quota"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
